import { Component, Host, JSX, Prop, State, h } from '@stencil/core'
import { TableInformations } from '~/selectors/types'

/**
 * WfWineScale is a component that represents a wine scale.
 * It provides a visual representation of various wine
 * characteristics such as taste, bearing, alcohol content,
 * sugar content, and acidity.
 *
 * @example
 * <wf-wine-scale characteristics={characteristics}/>
 */
@Component({
    tag: 'wf-wine-scale',
    styleUrl: 'wf-wine-scale.scss',
    shadow: true,
})
// eslint-disable-next-line require-jsdoc
export class WfWineScale {
    /**
     * The characteristics of the wine.
     */
    @Prop() characteristics?: TableInformations

    /**
     * State for the info card popovers.
     */
    @State() infoCardAcidOpen = false

    /**
     * State for the info card popovers.
     */
    @State() infoCardSugarOpen = false

    /*
    * Scales for the sugar and acid values.
    */
    private scales = {
        sugar: [2, 9, 18, 25, 45],
        acid: [3, 4, 5, 6, 8, 10],
    }

    /**
     * Returns the translation for a given characteristic name.
     */
    getTranslation(name: string): string {
        if (name === 'alcohol') return 'Alkoholgehalt'
        if (name === 'acid') return 'Säuregehalt'
        if (name === 'sugar') return 'Restsüße'
    }

    /**
     * Returns the value of a characteristic.
     */
    getCharacteristicValue(name: string): string[] {
        return this.characteristics?.find(c => c.name === name)?.value || []
    }

    /**
     * Returns the numeric value of a characteristic.
     */
    getNumericValueFromCharacteristics(name: string): number {
        const characteristic = this.characteristics?.find(c => c.name === name)?.value
        if (!characteristic) {
            return 0
        }

        return Number(characteristic[0].split(' ')[0].replace(',', '.').replace('%', '').trim())
    }

    /**
     * Calculates the percentage value on the scale for a given acid or sugar value.
     */
    calculateScaleValue(value: number, scale: number[]): number {
        value = Math.round(value)
        const nearestPointOnScale = scale.reduce((prev, curr) => {
            return Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev;
        });
        return (nearestPointOnScale / Math.max(...scale)) * 100
    }

    /**
     * Toggles the info card popover.
     */
    togglePopover(popover: string) {
        if (popover === 'acid') {
            this.infoCardAcidOpen = !this.infoCardAcidOpen
        } else if (popover === 'sugar') {
            this.infoCardSugarOpen = !this.infoCardSugarOpen
        }
    }

    /**
     * @inheritDoc
     */
    render(): JSX.Element {

        const taste = this.getCharacteristicValue('Schmeckt nach')
        if (!taste || taste.length === 0) {
            return
        }

        const bearing = this.getCharacteristicValue('Ausbau')
        const alcohol = this.getCharacteristicValue('Alkoholgehalt')

        const sugar = this.getCharacteristicValue('Restsüße')
        let sugarValue = undefined
        if (sugar.length > 0) {
            sugarValue = this.calculateScaleValue(
                this.getNumericValueFromCharacteristics('Restsüße'),
                this.scales.sugar)
        }

        const acid = this.getCharacteristicValue('Säuregehalt')
        let acidValue = undefined
        if (acid.length > 0) {
            acidValue = this.calculateScaleValue(
                this.getNumericValueFromCharacteristics('Säuregehalt'),
                this.scales.acid)
        }

        return (
            <Host>
                <div class="wine-scale">
                    <div class="wine-scale-entries">
                        <div class="wine-scale-entries-1">
                            {taste?.length > 0 && (
                                <div class="wine-scale-entry">
                                    <div class="wine-scale-entry-label">Geschmack</div>
                                    <div class="wine-scale-entry-value">
                                        {taste.slice(0, 2).map(t => (
                                            <span key={t}>{t}</span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div class="wine-scale-entries-2">
                            {bearing?.length > 0 && (
                                <div class="wine-scale-entry">
                                    <div class="wine-scale-entry-label">Ausbau</div>
                                    <div class="wine-scale-entry-value">
                                        {bearing.map(t => (
                                            <span key={t}>{t}</span>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {alcohol?.length > 0 && (
                                <div class="wine-scale-entry">
                                    <div class="wine-scale-entry-label">Alkohol</div>
                                    <div class="wine-scale-entry-value">
                                        {alcohol.map(t => (
                                            <span key={t}>{t.replace(' vol', '')}</span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div class="wine-scale-sliders">
                        {sugarValue && (
                            <div class="slider-container slider-container-1">
                                <label htmlFor="residual-sugar">
                                    Restsüße{' '}
                                    <div
                                        id="info-cirle-sugar"
                                        class="info-circle"
                                        onClick={() => this.togglePopover('sugar')}
                                    >
                                        <span class="info-icon">i</span>
                                    </div>
                                </label>
                                <vx-popover isOpen={this.infoCardSugarOpen} id="sugar-popover">
                                    <div class="info-tooltip">
                                        <h3 class="info-tooltip-headline">Was sagt die Restsüße aus?</h3>
                                        <div class="info-tooltip-text">
                                            {/* eslint-disable-next-line max-len */}
                                            <p>Je mehr Restsüße in einem Wein, desto süßer wirkt er am Gaumen. Dieses
                                                Empfinden wird geringer, je höher der Säuregehalt ist.</p>
                                            <a target='_blank' href='https://www.weinfreunde.de/magazin/weinwissen/restsuesse-was-vom-zucker-uebrigbleibt/?icid=onsite_pdp_weinskala_suesse_magazin' title='RESTSÜSSE: WAS VOM ZUCKER ÜBRIGBLEIBT'>Hier mehr über die Restsüße erfahren.</a>
                                        </div>
                                    </div>
                                </vx-popover>
                                <div class="scale-wrapper">
                                    <span class="scale-label left">Wenig</span>
                                    <div class="scale-inner-wrapper">
                                        <input disabled type="range" id="residual-sugar" min="0" max="10" step="1" value="3"/>
                                        <span class="value" style={{ left: `${sugarValue}%` }}>
                                            {sugar}
                                        </span>
                                    </div>
                                    <span class="scale-label right">Viel</span>
                                </div>
                            </div>
                        )}

                        {acidValue && (
                            <div class="slider-container slider-container-2">
                                <label htmlFor="acidity">
                                    Säuregehalt{' '}
                                    <div
                                        id="info-cirle-acid"
                                        class="info-circle"
                                        onClick={() => this.togglePopover('acid')}
                                    >
                                        <span class="info-icon">i</span>
                                    </div>
                                </label>
                                <vx-popover isOpen={this.infoCardAcidOpen} id="acid-popover">
                                    <div class="info-tooltip">
                                        <h3 class="info-tooltip-headline">Was sagt der Säuregehalt aus?</h3>
                                        <div class="info-tooltip-text">
                                            {/* eslint-disable-next-line max-len */}
                                            <p>Je höher der Säuregehalt eines Weines, desto frischer wirkt er am Gaumen
                                                und lässt die Restsüße moderater erscheinen.</p>
                                            <a target='_blank' href='https://www.weinfreunde.de/magazin/weinwissen/saeuregehalt-im-wein-der-schluessel-zu-geschmack-und-qualitaet/?icid=onsite_pdp_weinskala_saeure_magazin' title='SÄUREGEHALT IM WEIN: DER SCHLÜSSEL ZU GESCHMACK UND QUALITÄT'>Hier mehr über den Säuregehalt erfahren.</a>
                                        </div>
                                    </div>
                                </vx-popover>
                                <div class="scale-wrapper">
                                    <span class="scale-label left">Wenig</span>
                                    <div class="scale-inner-wrapper">
                                        <input disabled type="range" id="acidity" min="0" max="10" step="1" value="7"/>
                                        <span class="value" style={{ left: `${acidValue}%` }}>
                                            {acid}
                                        </span>
                                    </div>
                                    <span class="scale-label right">Viel</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </Host>
        )
    }
}
